import { HttpClient, HttpHeaders, HttpParams, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { RESTClientConfig } from '../config';
import { ApiRequestMethod } from '../utils/api-request-options';
import * as _ from 'lodash-es';
import { throwError } from 'rxjs';
import { map, timeout } from 'rxjs/operators';
import CryptoES from "crypto-es";
import * as i0 from "@angular/core";
import * as i1 from "@angular/common/http";
import * as i2 from "../config";
export class ApiService {
    constructor(http, config) {
        this.http = http;
        this.config = config;
        this.apiTimeOut = 30000;
        this.errorAlreadySent = false;
    }
    get(url, query, options = {}, pci = false) {
        options.query = query;
        options.method = ApiRequestMethod.get;
        options.headers = options.headers || {};
        return this.request(url, options, pci);
    }
    post(url, body, options = {}) {
        options.body = body;
        options.method = ApiRequestMethod.post;
        options.headers = options.headers || {};
        options.headers['Content-Type'] = options.headers['Content-Type'] || 'application/json';
        return this.request(url, options);
    }
    multipart(url, body, options = {}) {
        options.body = body;
        options.method = ApiRequestMethod.post;
        options.headers = options.headers || {};
        return this.request(url, options);
    }
    put(url, body, options = {}) {
        options.body = body;
        options.method = ApiRequestMethod.put;
        options.headers = options.headers || {};
        options.headers['Content-Type'] = 'application/json';
        return this.request(url, options);
    }
    patch(url, body, options = {}) {
        options.body = body;
        options.method = ApiRequestMethod.patch;
        options.headers = options.headers || {};
        options.headers['Content-Type'] = 'application/json';
        return this.request(url, options);
    }
    delete(url, body, options = {}) {
        options.method = ApiRequestMethod.delete;
        if (body) {
            options.body = body;
            options.headers = options.headers || {};
            options.headers['Content-Type'] = 'application/json';
        }
        return this.request(url, options);
    }
    notifyError(err, context, stateSnapshot) {
        if (!this.errorAlreadySent) {
            console.log("This error will be reported.");
            const healthUrl = this.buildUrl('/health/error', false);
            const body = {
                'salt': this.error_salt,
                'config': this.config ? this.config : "unknown",
                'error': err,
                'context': context,
                'snapshot': stateSnapshot,
                'datalayer': window['dataLayer']
            };
            this.http.post(healthUrl, body, { 'headers': { 'content-type': 'application/json' } }).subscribe();
            this.errorAlreadySent = true;
        }
        else {
            console.log("error already notified. skipping notification");
        }
    }
    request(url, options = {}, pci) {
        const apiTimeout = this.apiTimeOut;
        const httpOptions = {
            headers: new HttpHeaders(options.headers),
            params: this.buildSearchParams(options.query),
            observe: 'response',
            context: options.context
        };
        url = this.buildUrl(url, pci);
        if (options.method === ApiRequestMethod.get) {
            return this.http.get(url, httpOptions)
                .pipe(timeout(apiTimeout), map(response => {
                return this.handleHttpClientResponse(response, url, httpOptions);
            }));
        }
        else if (options.method === ApiRequestMethod.post) {
            return this.http.post(url, options.body, httpOptions)
                .pipe(timeout(apiTimeout), map(response => {
                return this.handleHttpClientResponse(response, url, httpOptions);
            }));
        }
        else if (options.method === ApiRequestMethod.put) {
            return this.http.put(url, options.body, httpOptions)
                .pipe(timeout(apiTimeout), map(response => {
                return this.handleHttpClientResponse(response, url, httpOptions);
            }));
        }
        else if (options.method === ApiRequestMethod.delete) {
            return this.http.delete(url, httpOptions)
                .pipe(timeout(apiTimeout), map(response => {
                return this.handleHttpClientResponse(response, url, httpOptions);
            }));
        }
        else if (options.method === ApiRequestMethod.options) {
            return this.http.options(url, httpOptions)
                .pipe(timeout(apiTimeout), map(response => {
                return this.handleHttpClientResponse(response, url, httpOptions);
            }));
        }
        else if (options.method === ApiRequestMethod.head) {
            return this.http.head(url, httpOptions)
                .pipe(timeout(apiTimeout), map(response => {
                return this.handleHttpClientResponse(response, url, httpOptions);
            }));
        }
        else if (options.method === ApiRequestMethod.patch) {
            return this.http.patch(url, httpOptions)
                .pipe(timeout(apiTimeout), map(response => {
                return this.handleHttpClientResponse(response, url, httpOptions);
            }));
        }
        else {
            return throwError(`Invalid API Call Method`);
        }
    }
    handleHttpClientResponse(response, url, httpOptions) {
        const body = (response && response.body) ? response.body : null;
        if (this.config.getAppWithUpgrades) {
            this.setUpgradeConfig(body);
        }
        if (body && body.status === 'ok') {
            return body;
        }
        else {
            const e = `API returned a malformed response with a 200 status. Error: ${response}`;
            throw new HttpErrorResponse({ error: e, status: 500, url: url, headers: httpOptions.headers });
        }
    }
    buildUrl(url, pci) {
        url = (!url.startsWith('/')) ? '/' + url : url;
        return (pci) ? `${this.config.pciApiUrl}${url}` : `${this.config.apiUrl}${url}`;
    }
    buildSearchParams(query) {
        let params = new HttpParams()
            .set('lang', this.config.lang ? this.config.lang : 'es')
            .set('app_version', this.config.appVersion)
            .set('app_name', this.config.appName)
            .set('device_type', this.config.deviceType);
        _.forEach(query, (value, param) => {
            if (!_.isNil(value)) {
                params = params.set(param, String(value));
            }
        });
        return params;
    }
    setUpgradeConfig(json) {
        if (json.force_upgrade) {
            this.config.setUpgradeType('force_upgrade');
        }
        else if (json.upgrade_recommended) {
            this.config.setUpgradeType('recommended_upgrade');
        }
        if (json.upgrade_message) {
            this.config.setUpgradeMessage(json.upgrade_message);
        }
    }
    get error_salt() {
        const zeroPad = (num, size) => {
            var s = "000000000" + num;
            return ("000000000" + num).substr(s.length - size);
        };
        const d = new Date();
        const year = d.getFullYear();
        const month = zeroPad(d.getMonth() + 1, 2);
        const day = zeroPad(d.getDate(), 2);
        const salt = `alert_${year}-${month}-${day}`;
        return CryptoES.MD5(salt).toString();
    }
}
ApiService.ɵfac = function ApiService_Factory(t) { return new (t || ApiService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(i2.RESTClientConfig)); };
ApiService.ɵprov = i0.ɵɵdefineInjectable({ token: ApiService, factory: ApiService.ɵfac });
(function () { i0.ɵsetClassMetadata(ApiService, [{
        type: Injectable
    }], function () { return [{ type: i1.HttpClient }, { type: i2.RESTClientConfig }]; }, null); })();
//# sourceMappingURL=api.service.js.map